export const DEFAULT_IMAGE_CROP = { x: 50, y: 50, zoom: 1 };
export const DEFAULT_HERO_BACKGROUND_DIM = 68;
export const DEFAULT_BACKGROUND_BLUR = 0;

export const DEFAULT_SITE_STYLE = {
  version: 1,
  paletteId: 'nexops',
  typography: 'modern',
  buttons: { style: 'solid', shape: 'rounded', size: 'regular' },
  cards: { shape: 'soft', frame: 'hairline', motion: 'lift' },
  motion: 'smooth',
};

export function normalizeImageCrop(crop) {
  const x = Number.isFinite(Number(crop?.x)) ? Math.max(0, Math.min(100, Number(crop.x))) : 50;
  const y = Number.isFinite(Number(crop?.y)) ? Math.max(0, Math.min(100, Number(crop.y))) : 50;
  const zoom = Number.isFinite(Number(crop?.zoom)) ? Math.max(1, Math.min(2.5, Number(crop.zoom))) : 1;
  return { x, y, zoom };
}

export function normalizeHeroBackgroundDim(value) {
  return Number.isFinite(Number(value)) ? Math.max(0, Math.min(90, Number(value))) : DEFAULT_HERO_BACKGROUND_DIM;
}

export function normalizeBackgroundBlur(value) {
  return Number.isFinite(Number(value)) ? Math.max(0, Math.min(24, Number(value))) : DEFAULT_BACKGROUND_BLUR;
}

export function cropFromLegacyPosition(position) {
  if (position === 'top') return { x: 50, y: 0, zoom: 1 };
  if (position === 'bottom') return { x: 50, y: 100, zoom: 1 };
  if (position === 'left') return { x: 0, y: 50, zoom: 1 };
  if (position === 'right') return { x: 100, y: 50, zoom: 1 };
  return { ...DEFAULT_IMAGE_CROP };
}

export function makeCmsId(prefix = 'cms') {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return `${prefix}-${crypto.randomUUID()}`;
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function emptySurface() { return { mode: 'auto' }; }

export function newContentMediaItem(url = '', placement = 'right') {
  return { id: makeCmsId('media'), url, crop: { ...DEFAULT_IMAGE_CROP }, placement, size: 'large', width: placement === 'above' || placement === 'below' ? 100 : 40, ratio: 'original', fit: 'cover', align: 'center' };
}

export function newHeroBlock() {
  return {
    id: makeCmsId('hero'), type: 'hero', eyebrow: '', title: 'A clear headline that says what matters.', body: '',
    rotating: [], imagePosition: 'center', imageCrop: { ...DEFAULT_IMAGE_CROP }, mediaSide: 'right',
    action: { label: 'Learn more', href: '#' }, surface: emptySurface(), backgroundDim: DEFAULT_HERO_BACKGROUND_DIM,
    backgroundBlur: DEFAULT_BACKGROUND_BLUR, effect: 'inherit',
  };
}

export function newContentBlock() {
  return {
    id: makeCmsId('content'), type: 'content', eyebrow: '', title: 'Text + media',
    body: 'Add the content. Add an image if you need one. The layout, proportions and spacing are handled automatically.',
    bodyAlign: 'left', media: [], sectionWidth: 'full', sectionPosition: 'center', imagePosition: 'center',
    imageCrop: { ...DEFAULT_IMAGE_CROP }, mediaSide: 'auto', action: undefined, surface: emptySurface(),
    backgroundDim: DEFAULT_HERO_BACKGROUND_DIM, backgroundBlur: DEFAULT_BACKGROUND_BLUR, effect: 'inherit',
  };
}

export function newCardItem(index = 0) {
  return {
    id: makeCmsId('card'), title: `Card ${index + 1}`, body: 'Short supporting copy.', imagePosition: 'center',
    imageCrop: { ...DEFAULT_IMAGE_CROP }, surface: emptySurface(), effect: 'inherit',
  };
}

export function newCardsBlock() {
  return {
    id: makeCmsId('cards'), type: 'cards', eyebrow: '', title: 'Cards', intro: '', columns: 'auto',
    rotate: false, rotateVisible: 4, rotateInterval: 5000,
    items: [newCardItem(0), newCardItem(1), newCardItem(2)], sectionWidth: 'full', sectionPosition: 'center',
    surface: emptySurface(), effect: 'inherit',
  };
}

export function newCtaBlock() {
  return {
    id: makeCmsId('cta'), type: 'cta', eyebrow: '', title: 'Ready for the next step?', body: '',
    action: { label: 'Get in touch', href: '/#contact' }, sectionWidth: 'full', sectionPosition: 'center',
    surface: emptySurface(), effect: 'inherit',
  };
}

export function newContactBlock() {
  return {
    id: makeCmsId('contact'), type: 'contact', eyebrow: '', title: 'Contact us', body: '',
    fields: {
      nameLabel: 'Name', emailLabel: 'Email', phoneLabel: 'Phone', messageLabel: 'Message', phoneRequired: false,
      consentText: 'I consent to the processing of my personal data in accordance with the Privacy Policy.',
      privacyLabel: 'Privacy Policy', submitLabel: 'Send message',
    },
    surface: emptySurface(), effect: 'inherit',
  };
}

export function newFaqItem(index = 0) {
  return { id: makeCmsId('faq-item'), question: `Question ${index + 1}`, answer: 'Add the answer here.' };
}

export function newFaqBlock() {
  return {
    id: makeCmsId('faq'), type: 'faq', eyebrow: '', title: 'Frequently asked questions', intro: '',
    items: [newFaqItem(0), newFaqItem(1), newFaqItem(2)], surface: emptySurface(), effect: 'inherit',
  };
}

export function newTreatmentsBlock() {
  return {
    id: makeCmsId('treatments'), type: 'treatments', eyebrow: '', title: 'Treatments', intro: '', departmentId: '',
    serviceCardDefaults: {}, serviceCardStyles: {}, sectionWidth: 'full', sectionPosition: 'center', surface: emptySurface(), effect: 'inherit',
  };
}

export function newCoursesBlock() {
  return {
    id: makeCmsId('courses'), type: 'courses', eyebrow: '', title: 'Courses / training', intro: '', selectedCourseIds: [],
    action: undefined, sectionWidth: 'full', sectionPosition: 'center', surface: emptySurface(), effect: 'inherit',
  };
}

export function newContactMapBlock() {
  return {
    id: makeCmsId('contact-map'), type: 'contact_map', eyebrow: '', title: 'Contact', body: '', phone: '', phone2: '',
    email: '', address: '', hours: '', mapUrl: '', sectionWidth: 'full', sectionPosition: 'center',
    surface: emptySurface(), effect: 'inherit',
  };
}

export function newLatestPostsBlock() {
  return {
    id: makeCmsId('latest-posts'), type: 'latest_posts', eyebrow: '', title: 'Latest from the blog', intro: '', limit: 3,
    sectionWidth: 'full', sectionPosition: 'center', surface: emptySurface(), effect: 'inherit',
  };
}

export function newBookingCtaBlock() {
  return {
    id: makeCmsId('booking-cta'), type: 'booking_cta', eyebrow: '', title: 'Book an appointment', body: '',
    buttonLabel: 'Book now', departmentId: '', sectionWidth: 'full', sectionPosition: 'center',
    surface: emptySurface(), effect: 'inherit',
  };
}

export const SITE_CMS_BLOCK_TYPES = [
  ['hero', 'Hero'], ['content', 'Text + media'], ['cards', 'Cards'], ['cta', 'CTA'], ['contact', 'Contact Form'], ['faq', 'FAQ'],
  ['treatments', 'Treatments'], ['courses', 'Courses / training'], ['contact_map', 'Contact + map'], ['latest_posts', 'Latest blog posts'], ['booking_cta', 'Booking CTA'],
];

export function newBlock(type) {
  if (type === 'hero') return newHeroBlock();
  if (type === 'content') return newContentBlock();
  if (type === 'cards') return newCardsBlock();
  if (type === 'cta') return newCtaBlock();
  if (type === 'contact') return newContactBlock();
  if (type === 'faq') return newFaqBlock();
  if (type === 'treatments') return newTreatmentsBlock();
  if (type === 'courses') return newCoursesBlock();
  if (type === 'contact_map') return newContactMapBlock();
  if (type === 'latest_posts') return newLatestPostsBlock();
  if (type === 'booking_cta') return newBookingCtaBlock();
  return null;
}

export function newPageDocument() {
  return { version: 1, blocks: [newHeroBlock(), newContentBlock(), newCardsBlock(), newCtaBlock()] };
}

const surfaceFromLegacy = data => {
  if (data?._layout?.background_image) return { mode: 'image', imageUrl: data._layout.background_image, crop: { ...DEFAULT_IMAGE_CROP } };
  return emptySurface();
};

function legacyAction(label, href) {
  return label && href ? { label, href } : undefined;
}

export function legacySectionToBlock(section, index = 0) {
  const d = section?.data || {};
  const id = section?.id || makeCmsId(section?.type || 'legacy');
  if (section?.type === 'hero') return {
    ...newHeroBlock(), id, eyebrow: d.eyebrow || '', title: d.title || '', body: String(d.subtitle || '').replace(/<[^>]+>/g, ''),
    rotating: Array.isArray(d.rotating) ? d.rotating.map(item => typeof item === 'string' ? item : item?.text).filter(Boolean) : [],
    imageUrl: d.image || undefined, imageCrop: { x: Number(d.focal_x ?? 50), y: Number(d.focal_y ?? 50), zoom: 1 },
    mediaSide: d.image_position === 'left' ? 'left' : 'right', action: legacyAction(d.cta_label, d.cta_link),
    surface: d.image_position === 'background' && d.image ? { mode: 'image', imageUrl: d.image, crop: { x: Number(d.focal_x ?? 50), y: Number(d.focal_y ?? 50), zoom: 1 } } : surfaceFromLegacy(d),
    backgroundDim: Number(d.background_dim ?? DEFAULT_HERO_BACKGROUND_DIM), backgroundBlur: Number(d.background_blur ?? 0),
  };
  if (section?.type === 'text_media' || section?.type === 'text') return {
    ...newContentBlock(), id, eyebrow: d.eyebrow || '', title: d.heading || d.title || '', body: String(d.body || d.content || '').replace(/<[^>]+>/g, ''),
    imageUrl: d.image || undefined, imageCrop: { x: Number(d.focal_x ?? 50), y: Number(d.focal_y ?? 50), zoom: 1 },
    mediaSide: String(d.media_side || d.image_position || 'auto').toLowerCase(), action: legacyAction(d.cta_label, d.cta_link), surface: surfaceFromLegacy(d),
  };
  if (section?.type === 'cards') return {
    ...newCardsBlock(), id, eyebrow: d.eyebrow || '', title: d.title || '', intro: d.intro || '', columns: d.columns || 'auto',
    rotate: Boolean(d.rotate), rotateVisible: Number(d.rotate_visible || 4), rotateInterval: Number(d.rotate_interval || 5000),
    items: (d.items || []).map((item, cardIndex) => ({ ...newCardItem(cardIndex), id: item.id || makeCmsId('card'), title: item.title || '', body: item.body || item.text || '', imageUrl: item.image || item.imageUrl || undefined, link: legacyAction(item.button_label || item.link?.label, item.button_link || item.link?.href) })),
    surface: surfaceFromLegacy(d),
  };
  if (section?.type === 'cta') return { ...newCtaBlock(), id, eyebrow: d.eyebrow || '', title: d.title || '', body: d.text || d.body || '', action: legacyAction(d.button_label, d.button_link), surface: surfaceFromLegacy(d) };
  if (section?.type === 'contact_form') return { ...newContactBlock(), id, eyebrow: d.eyebrow || '', title: d.title || '', body: d.subtitle || '', fields: { ...newContactBlock().fields, submitLabel: d.button_label || 'Send message' }, surface: surfaceFromLegacy(d) };
  if (section?.type === 'faq') return { ...newFaqBlock(), id, eyebrow: d.eyebrow || '', title: d.title || '', intro: d.intro || '', items: (d.items || []).map((item, itemIndex) => ({ id: item.id || makeCmsId('faq-item'), question: item.question || '', answer: item.answer || '' })), surface: surfaceFromLegacy(d) };
  if (section?.type === 'services') return { ...newTreatmentsBlock(), id, eyebrow: d.eyebrow || '', title: d.title || 'Treatments', intro: d.subtitle || '', departmentId: d.department_id || '', surface: surfaceFromLegacy(d) };
  if (section?.type === 'courses') return { ...newCoursesBlock(), id, eyebrow: d.eyebrow || '', title: d.title || 'Courses / training', intro: String(d.intro || '').replace(/<[^>]+>/g, ''), selectedCourseIds: Array.isArray(d.selected_courses) ? d.selected_courses.map(item => item?.course_id).filter(Boolean) : [], action: legacyAction(d.cta_label, d.cta_link), surface: surfaceFromLegacy(d) };
  if (section?.type === 'contact') return { ...newContactMapBlock(), id, eyebrow: d.eyebrow || '', title: d.title || 'Contact', body: d.text || '', phone: d.phone || '', phone2: d.phone2 || '', email: d.email || '', address: d.address || '', hours: d.hours || '', mapUrl: d.map_url || '', surface: surfaceFromLegacy(d) };
  if (section?.type === 'latest_news') return { ...newLatestPostsBlock(), id, eyebrow: d.eyebrow || '', title: d.title || 'Latest from the blog', limit: Number(d.limit || 3), surface: surfaceFromLegacy(d) };
  if (section?.type === 'booking_cta') return { ...newBookingCtaBlock(), id, eyebrow: d.eyebrow || '', title: d.title || 'Book an appointment', body: d.text || '', buttonLabel: d.button_label || 'Book now', departmentId: d.department_id || '', surface: surfaceFromLegacy(d) };
  return { id, type: 'legacy', legacyType: section?.type || 'unknown', legacySection: { ...section, order: index } };
}

export function legacySectionsToDocument(sections) {
  return { version: 1, blocks: (Array.isArray(sections) ? sections : []).map((section, index) => legacySectionToBlock(section, index)) };
}

export function normalizeDocument(document, legacySections = []) {
  if (document && document.version === 1 && Array.isArray(document.blocks)) return document;
  return legacySectionsToDocument(legacySections);
}
